import { NgModule, inject } from '@angular/core';
import { RouterModule } from '@angular/router';

import { RouteTag } from '@frontend/sports/common/core/data-access/route';

import { SportRoute } from '../router/multi-route.model';
import { nameIdentifierMatcher } from '../router/name-identifier.matcher';
import { paramsOrQueryParamsChange } from '../router/router.exports';
import { use } from '../router/router.resolver';
import { SitemapRoutingToken } from '../sitemap/sitemap-routing';
import { LiveBreadcrumbsResolver } from './live-breadcrumbs.resolver';
import { liveEsportsPreResolver, liveEsportsResolver } from './live-esports.resolver';
import { LiveEventListComponent } from './live-event-list.component';
import { LiveEventListResolver } from './live-event-list.resolver';
import { LiveFavouriteResolver } from './live-favourite.resolver';
import { LiveFavouritesOverviewComponent } from './live-favourites-overview.component';
import { LiveRootGuard, LiveSportGuard } from './live-guard.service';
import { LiveHighlightResolver } from './live-highlight.resolver';
import { LiveOverviewSubNavigationResolve } from './live-overview-subnavigation.service';
import { LiveOverviewComponent } from './live-overview.component';
import { LiveSportListComponent } from './live-sport-list.component';
import { LiveSportListResolver } from './live-sport-list.resolver';
import { LiveSportNavigationService } from './live-sport-navigation.service';
import { LiveStreamResolver } from './live-stream.resolver';
import { LiveComponent } from './live.component';

const routes: SportRoute[] = [
    {
        path: '',
        component: LiveComponent,
        data: {
            tag: RouteTag.Live,
            breadcrumbs: LiveBreadcrumbsResolver,
            subNavigation: LiveOverviewSubNavigationResolve,
            sitemap: SitemapRoutingToken.LiveSitemapResolver,
        },
        providers: [LiveSportNavigationService],
        resolve: {
            layout: () => {
                const liveSportNavigationService = inject(LiveSportNavigationService);
                liveSportNavigationService.init();
            },
        },
        children: [
            {
                path: '',
                canActivate: [LiveRootGuard],
                component: LiveSportListComponent,
                resolve: use(LiveSportListResolver),
                data: {
                    preResolve: () => {
                        inject(LiveSportListResolver).preResolve();
                    },
                },
            },
            {
                path: '{all}',
                data: {
                    tag: RouteTag.LiveAll,
                    persist: true,
                    preResolve: () => {
                        inject(LiveSportListResolver).preResolve();
                    },
                },
                component: LiveSportListComponent,
                resolve: use(LiveSportListResolver),
            },
            {
                path: '{video}',
                data: {
                    tag: RouteTag.LiveVideo,
                    persist: true,
                    preResolve: () => {
                        inject(LiveStreamResolver).preResolve();
                    },
                },
                component: LiveOverviewComponent,
                resolve: use(LiveStreamResolver),
            },
            {
                path: '{betting}',
                data: {
                    tag: RouteTag.LiveHighlights,
                    persist: true,
                    preResolve: () => {
                        inject(LiveHighlightResolver).preResolve();
                    },
                },
                component: LiveOverviewComponent,
                resolve: use(LiveHighlightResolver),
            },
            {
                path: '{favourites}',
                data: {
                    persist: true,
                    preResolve: () => {
                        inject(LiveFavouriteResolver).preResolve();
                    },
                },
                component: LiveFavouritesOverviewComponent,
                resolve: use(LiveFavouriteResolver),
            },
            {
                path: '{esports}',
                data: {
                    tag: RouteTag.LiveEsports,
                    persist: true,
                    preResolve: liveEsportsPreResolver,
                },
                component: LiveOverviewComponent,
                resolve: { model: liveEsportsResolver },
            },
            {
                matcher: nameIdentifierMatcher,
                data: {
                    matcherParam: 'sport',
                },
                children: [
                    {
                        path: '',
                        runGuardsAndResolvers: paramsOrQueryParamsChange,
                        canActivate: [LiveSportGuard],
                        data: { tag: RouteTag.LiveSport, persist: true },
                        component: LiveEventListComponent,
                        resolve: use(LiveEventListResolver),
                    },
                    {
                        matcher: nameIdentifierMatcher,
                        data: {
                            matcherParam: 'region',
                            tag: RouteTag.LiveRegion,
                            persist: true,
                        },

                        children: [
                            {
                                path: '',
                                component: LiveEventListComponent,
                                resolve: use(LiveEventListResolver),
                            },

                            {
                                matcher: nameIdentifierMatcher,
                                data: {
                                    matcherParam: 'league',
                                    tag: RouteTag.LiveLeague,
                                    persist: true,
                                },

                                children: [
                                    {
                                        path: '',
                                        component: LiveEventListComponent,
                                        resolve: use(LiveEventListResolver),
                                    },

                                    {
                                        matcher: nameIdentifierMatcher,
                                        data: {
                                            matcherParam: 'conference',
                                            tag: RouteTag.LiveConference,
                                            persist: true,
                                        },
                                        component: LiveEventListComponent,
                                        resolve: use(LiveEventListResolver),
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class LiveRoutingModule {}
